import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Tabs, Tab,
  InputGroup, DropdownButton, Dropdown
} from 'react-bootstrap';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const ContactManager = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone_main: '',
    phone_alternate: '',
    fax: '',
    toll_free: '',
    email_general: '',
    email_admissions: '',
    email_info: '',
    email_support: '',
    website_url: '',
    facebook_url: '',
    twitter_url: '',
    linkedin_url: '',
    instagram_url: '',
    youtube_url: '',
    mon_fri_hours: '',
    saturday_hours: '',
    sunday_hours: '',
    holiday_hours: '',
    latitude: '',
    longitude: '',
    google_maps_embed_code: '',
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchContacts();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      phone_main: '',
      phone_alternate: '',
      fax: '',
      toll_free: '',
      email_general: '',
      email_admissions: '',
      email_info: '',
      email_support: '',
      website_url: '',
      facebook_url: '',
      twitter_url: '',
      linkedin_url: '',
      instagram_url: '',
      youtube_url: '',
      mon_fri_hours: '',
      saturday_hours: '',
      sunday_hours: '',
      holiday_hours: '',
      latitude: '',
      longitude: '',
      google_maps_embed_code: '',
      display_order: 0,
      is_active: true
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/contact_info.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedContacts = response.data.data.map(contact => ({
          ...contact,
          id: parseInt(contact.id),
          display_order: parseInt(contact.display_order),
          is_active: contact.is_active === "1" || contact.is_active === 1
        })).sort((a, b) => a.display_order - b.display_order);
        
        setContacts(processedContacts);
      } else {
        setError('Failed to load contacts');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      let response;
      
      // If order is not specified, set to next available
      if (!formData.display_order || formData.display_order <= 0) {
        const maxOrder = contacts.length > 0 
          ? Math.max(...contacts.map(contact => parseInt(contact.display_order))) 
          : 0;
        formData.display_order = maxOrder + 1;
      }
      
      if (editingContact) {
        response = await axios.put(
          `${API_BASE_URL}/contact_info.php?id=${editingContact.id}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/contact_info.php`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingContact ? 'Contact updated!' : 'Contact added!');
        setShowModal(false);
        resetForm();
        setEditingContact(null);
        setActiveTab('basic');
        fetchContacts();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving contact: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/contact_info.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Contact deleted!');
          fetchContacts();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete contact: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting contact');
        console.error(err);
      }
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name || '',
      address_line1: contact.address_line1 || '',
      address_line2: contact.address_line2 || '',
      city: contact.city || '',
      state: contact.state || '',
      postal_code: contact.postal_code || '',
      country: contact.country || '',
      phone_main: contact.phone_main || '',
      phone_alternate: contact.phone_alternate || '',
      fax: contact.fax || '',
      toll_free: contact.toll_free || '',
      email_general: contact.email_general || '',
      email_admissions: contact.email_admissions || '',
      email_info: contact.email_info || '',
      email_support: contact.email_support || '',
      website_url: contact.website_url || '',
      facebook_url: contact.facebook_url || '',
      twitter_url: contact.twitter_url || '',
      linkedin_url: contact.linkedin_url || '',
      instagram_url: contact.instagram_url || '',
      youtube_url: contact.youtube_url || '',
      mon_fri_hours: contact.mon_fri_hours || '',
      saturday_hours: contact.saturday_hours || '',
      sunday_hours: contact.sunday_hours || '',
      holiday_hours: contact.holiday_hours || '',
      latitude: contact.latitude || '',
      longitude: contact.longitude || '',
      google_maps_embed_code: contact.google_maps_embed_code || '',
      display_order: parseInt(contact.display_order) || 0,
      is_active: contact.is_active === true || contact.is_active === "1" || contact.is_active === 1
    });
    
    setShowModal(true);
    setActiveTab('basic');
  };

  const handleToggleActive = async (contact) => {
    try {
      const currentActive = contact.is_active === true || contact.is_active === "1" || contact.is_active === 1;
      const updatedData = {
        name: contact.name || '',
        address_line1: contact.address_line1 || '',
        address_line2: contact.address_line2 || '',
        city: contact.city || '',
        state: contact.state || '',
        postal_code: contact.postal_code || '',
        country: contact.country || '',
        phone_main: contact.phone_main || '',
        phone_alternate: contact.phone_alternate || '',
        fax: contact.fax || '',
        toll_free: contact.toll_free || '',
        email_general: contact.email_general || '',
        email_admissions: contact.email_admissions || '',
        email_info: contact.email_info || '',
        email_support: contact.email_support || '',
        website_url: contact.website_url || '',
        facebook_url: contact.facebook_url || '',
        twitter_url: contact.twitter_url || '',
        linkedin_url: contact.linkedin_url || '',
        instagram_url: contact.instagram_url || '',
        youtube_url: contact.youtube_url || '',
        mon_fri_hours: contact.mon_fri_hours || '',
        saturday_hours: contact.saturday_hours || '',
        sunday_hours: contact.sunday_hours || '',
        holiday_hours: contact.holiday_hours || '',
        latitude: contact.latitude || '',
        longitude: contact.longitude || '',
        google_maps_embed_code: contact.google_maps_embed_code || '',
        display_order: parseInt(contact.display_order) || 0,
        is_active: !currentActive ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/contact_info.php?id=${contact.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchContacts();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContact(null);
    resetForm();
    setActiveTab('basic');
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(contacts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index + 1
    }));
    
    setContacts(updatedItems);
    
    // Send update to server
    try {
      const token = localStorage.getItem('token');
      const reorderData = updatedItems.map(item => ({
        id: item.id,
        display_order: item.display_order
      }));
      
      const response = await axios.post(`${API_BASE_URL}/contact_info.php`, {
        reorder: reorderData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.success) {
        setError('Failed to save order changes');
        fetchContacts(); // Revert to server state
      }
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to save order changes');
      fetchContacts(); // Revert to server state
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phone;
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading contacts...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Contact Information Manager</h2>
            <p className="text-muted">Manage multiple contact locations and information</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              const nextOrder = contacts.length > 0 
                ? Math.max(...contacts.map(contact => parseInt(contact.display_order))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                display_order: nextOrder,
                is_active: true
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Contact Location
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="contacts">
                {(provided) => (
                  <Table responsive hover ref={provided.innerRef} {...provided.droppableProps}>
                    <thead>
                      <tr>
                        <th width="60">Drag</th>
                        <th>Name/Location</th>
                        <th>Address</th>
                        <th>Phone/Email</th>
                        <th>Social Media</th>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th width="120">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact, index) => (
                        <Draggable key={contact.id} draggableId={contact.id.toString()} index={index}>
                          {(provided) => (
                            <tr 
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <td {...provided.dragHandleProps}>
                                <i className="fas fa-grip-vertical text-muted"></i>
                              </td>
                              <td>
                                <strong>{contact.name}</strong><br />
                                <small className="text-muted">
                                  {contact.city}, {contact.state}
                                </small>
                              </td>
                              <td>
                                <small>
                                  {truncateText(contact.address_line1, 30)}<br />
                                  {contact.city && `${contact.city}, `}{contact.state}
                                </small>
                              </td>
                              <td>
                                <small>
                                  {contact.phone_main && (
                                    <>
                                      <i className="fas fa-phone text-primary me-1"></i>
                                      {formatPhoneNumber(contact.phone_main)}<br />
                                    </>
                                  )}
                                  {contact.email_general && (
                                    <>
                                      <i className="fas fa-envelope text-primary me-1"></i>
                                      {truncateText(contact.email_general, 20)}
                                    </>
                                  )}
                                </small>
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  {contact.facebook_url && (
                                    <Badge bg="primary" className="me-1">
                                      <i className="fab fa-facebook-f"></i>
                                    </Badge>
                                  )}
                                  {contact.twitter_url && (
                                    <Badge bg="info" className="me-1">
                                      <i className="fab fa-twitter"></i>
                                    </Badge>
                                  )}
                                  {contact.linkedin_url && (
                                    <Badge bg="linkedin" className="me-1">
                                      <i className="fab fa-linkedin-in"></i>
                                    </Badge>
                                  )}
                                  {contact.instagram_url && (
                                    <Badge bg="instagram" className="me-1">
                                      <i className="fab fa-instagram"></i>
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Badge bg="secondary">{contact.display_order}</Badge>
                              </td>
                              <td>
                                <Form.Check
                                  type="switch"
                                  checked={contact.is_active === true || contact.is_active === "1" || contact.is_active === 1}
                                  onChange={() => handleToggleActive(contact)}
                                  label={(contact.is_active === true || contact.is_active === "1" || contact.is_active === 1) ? 'Active' : 'Inactive'}
                                  key={`active-${contact.id}-${contact.is_active}`}
                                />
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatDate(contact.created_at)}
                                </small>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEdit(contact)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(contact.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </Table>
                )}
              </Droppable>
            </DragDropContext>
            
            {contacts.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-map-marker-alt fa-3x mb-3"></i>
                <p>No contact information found. Add your first contact location!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Contact Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingContact ? 'Edit Contact Information' : 'Add Contact Information'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
              >
                <Tab eventKey="basic" title="Basic Info">
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name/Location *</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="e.g., Main Campus, Administration Office"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Display Order</Form.Label>
                        <Form.Control
                          type="number"
                          name="display_order"
                          value={formData.display_order}
                          onChange={handleInputChange}
                          min="1"
                        />
                        <Form.Text className="text-muted">
                          Lower numbers appear first
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 1</Form.Label>
                    <Form.Control
                      type="text"
                      name="address_line1"
                      value={formData.address_line1}
                      onChange={handleInputChange}
                      placeholder="Street address, P.O. box"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="address_line2"
                      value={formData.address_line2}
                      onChange={handleInputChange}
                      placeholder="Apartment, suite, unit, building, floor"
                    />
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="City"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>State/Province</Form.Label>
                        <Form.Control
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="State/Province"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Postal Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="postal_code"
                          value={formData.postal_code}
                          onChange={handleInputChange}
                          placeholder="ZIP/Postal code"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          placeholder="Country"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive contacts won't be shown on the website
                    </Form.Text>
                  </Form.Group>
                </Tab>
                
                <Tab eventKey="contact" title="Contact Details">
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Main Phone *</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone_main"
                          value={formData.phone_main}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Alternate Phone</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone_alternate"
                          value={formData.phone_alternate}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7891"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Fax</Form.Label>
                        <Form.Control
                          type="text"
                          name="fax"
                          value={formData.fax}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7892"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Toll Free</Form.Label>
                        <Form.Control
                          type="text"
                          name="toll_free"
                          value={formData.toll_free}
                          onChange={handleInputChange}
                          placeholder="1-800-XXX-XXXX"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>General Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email_general"
                      value={formData.email_general}
                      onChange={handleInputChange}
                      placeholder="info@example.com"
                    />
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Admissions Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email_admissions"
                          value={formData.email_admissions}
                          onChange={handleInputChange}
                          placeholder="admissions@example.com"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Information Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email_info"
                          value={formData.email_info}
                          onChange={handleInputChange}
                          placeholder="information@example.com"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Support Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email_support"
                          value={formData.email_support}
                          onChange={handleInputChange}
                          placeholder="support@example.com"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Website URL</Form.Label>
                        <Form.Control
                          type="url"
                          name="website_url"
                          value={formData.website_url}
                          onChange={handleInputChange}
                          placeholder="https://example.com"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>
                
                <Tab eventKey="hours" title="Hours & Social">
                  <Row className="mt-3">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mon-Fri Hours</Form.Label>
                        <Form.Control
                          type="text"
                          name="mon_fri_hours"
                          value={formData.mon_fri_hours}
                          onChange={handleInputChange}
                          placeholder="9:00 AM - 5:00 PM"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Saturday Hours</Form.Label>
                        <Form.Control
                          type="text"
                          name="saturday_hours"
                          value={formData.saturday_hours}
                          onChange={handleInputChange}
                          placeholder="9:00 AM - 1:00 PM"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sunday Hours</Form.Label>
                        <Form.Control
                          type="text"
                          name="sunday_hours"
                          value={formData.sunday_hours}
                          onChange={handleInputChange}
                          placeholder="Closed"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Holiday Hours</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="holiday_hours"
                      value={formData.holiday_hours}
                      onChange={handleTextareaChange}
                      rows={3}
                      placeholder="Special holiday hours or notes..."
                    />
                  </Form.Group>
                  
                  <h6 className="mt-4 mb-3">Social Media URLs</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Facebook</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <i className="fab fa-facebook text-primary"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="url"
                            name="facebook_url"
                            value={formData.facebook_url}
                            onChange={handleInputChange}
                            placeholder="https://facebook.com/username"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Twitter</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <i className="fab fa-twitter text-info"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="url"
                            name="twitter_url"
                            value={formData.twitter_url}
                            onChange={handleInputChange}
                            placeholder="https://twitter.com/username"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>LinkedIn</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <i className="fab fa-linkedin text-linkedin"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="url"
                            name="linkedin_url"
                            value={formData.linkedin_url}
                            onChange={handleInputChange}
                            placeholder="https://linkedin.com/company/username"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Instagram</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <i className="fab fa-instagram text-instagram"></i>
                          </InputGroup.Text>
                          <Form.Control
                            type="url"
                            name="instagram_url"
                            value={formData.instagram_url}
                            onChange={handleInputChange}
                            placeholder="https://instagram.com/username"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>YouTube</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <i className="fab fa-youtube text-danger"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="url"
                        name="youtube_url"
                        value={formData.youtube_url}
                        onChange={handleInputChange}
                        placeholder="https://youtube.com/c/username"
                      />
                    </InputGroup>
                  </Form.Group>
                </Tab>
                
                <Tab eventKey="map" title="Map & Coordinates">
                  <Row className="mt-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Latitude</Form.Label>
                        <Form.Control
                          type="text"
                          name="latitude"
                          value={formData.latitude}
                          onChange={handleInputChange}
                          placeholder="40.7128"
                        />
                        <Form.Text className="text-muted">
                          Decimal format (e.g., 40.7128)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Longitude</Form.Label>
                        <Form.Control
                          type="text"
                          name="longitude"
                          value={formData.longitude}
                          onChange={handleInputChange}
                          placeholder="-74.0060"
                        />
                        <Form.Text className="text-muted">
                          Decimal format (e.g., -74.0060)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Google Maps Embed Code</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="google_maps_embed_code"
                      value={formData.google_maps_embed_code}
                      onChange={handleTextareaChange}
                      rows={6}
                      placeholder='<iframe src="https://www.google.com/maps/embed?..." width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy"></iframe>'
                    />
                    <Form.Text className="text-muted">
                      Paste the iframe embed code from Google Maps
                    </Form.Text>
                  </Form.Group>
                </Tab>
              </Tabs>
              
              {editingContact && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <div className="row">
                      <div className="col-md-6">
                        <i className="fas fa-calendar-plus me-2"></i>
                        Created: {formatDate(editingContact.created_at)}
                      </div>
                      <div className="col-md-6">
                        <i className="fas fa-sync-alt me-2"></i>
                        Last Updated: {formatDate(editingContact.updated_at)}
                      </div>
                    </div>
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingContact ? 'Update' : 'Add'} Contact
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default ContactManager;